/**
 * CPA-ONBOARDING-1 — Firm Profile.
 *
 * The firm's legal/professional identity, captured once and reused wherever a
 * report cover page, engagement letter, or portal footer needs to say who
 * signed the work. This is NOT the white-label look-and-feel screen — that is
 * BrandRegistrationPage (slug, colors, custom domain). The two are deliberately
 * separate; the only place they touch is the logo, and this page reuses the
 * SAME upload endpoint (POST /v1/documents/upload-logo) rather than adding a
 * second logo path.
 *
 * Governance: a profile is saved as a `draft` — half-finished is fine, a CPA
 * fills it in over more than one sitting. `Activate` is the draft -> active
 * transition, and only an active profile is visible to downstream branding.
 * The backend returns 422 with a list of what is still missing.
 */
import { useEffect, useState } from 'react'
import { apiClient } from '@/api/client'
import { Button } from '@/components/ui/button'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { FirmIdentityCard } from './firmProfile/FirmIdentityCard'
import { FirmDetailsCards } from './firmProfile/FirmDetailsCards'
import { EMPTY_FORM, extractErrors, toForm } from './firmProfile/model'
import type { FirmProfile, FirmProfileResponse, FormState } from './firmProfile/model'

export function FirmProfilePage() {
  const [form, setForm] = useState<FormState>({ ...EMPTY_FORM })
  const [status, setStatus] = useState<string>('draft')
  const [hasProfile, setHasProfile] = useState(false)
  const [needsOnboarding, setNeedsOnboarding] = useState(false)
  const [licenseStates, setLicenseStates] = useState<string[]>([])
  const [updatedAt, setUpdatedAt] = useState<string | null>(null)

  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [activating, setActivating] = useState(false)
  const [uploading, setUploading] = useState(false)
  const [errors, setErrors] = useState<string[]>([])
  const [notice, setNotice] = useState<string | null>(null)

  useEffect(() => {
    apiClient.get<FirmProfileResponse>('/v1/firm-profile')
      .then(res => applyLoaded(res.data))
      .catch(err => setErrors(extractErrors(err)))
      .finally(() => setLoading(false))
  }, [])

  function applyLoaded(data: FirmProfileResponse) {
    setForm(toForm(data.profile))
    setStatus(data.profile?.profile_status ?? 'draft')
    setHasProfile(data.profile !== null)
    setNeedsOnboarding(data.needs_onboarding)
    setLicenseStates(data.license_states ?? [])
    setUpdatedAt(data.profile?.updated_at ?? null)
  }

  function applySaved(profile: FirmProfile) {
    setForm(toForm(profile))
    setStatus(profile.profile_status)
    setHasProfile(true)
    setNeedsOnboarding(profile.profile_status !== 'active')
    setUpdatedAt(profile.updated_at)
  }

  const update = (field: keyof FormState, value: string) =>
    setForm(prev => ({ ...prev, [field]: value }))

  async function handleLogoUpload(file: File) {
    setUploading(true)
    setErrors([])
    try {
      const body = new FormData()
      body.append('file', file)
      // Same endpoint BrandRegistrationPage uses — one logo path, not two.
      const res = await apiClient.post<{ logo_url: string }>('/v1/documents/upload-logo', body, {
        headers: { 'Content-Type': 'multipart/form-data' },
      })
      update('firm_logo_ref', res.data.logo_url)
    } catch (err) {
      setErrors(extractErrors(err))
    } finally {
      setUploading(false)
    }
  }

  async function handleSave() {
    setSaving(true)
    setErrors([])
    setNotice(null)
    try {
      const res = await apiClient.put<FirmProfile>('/v1/firm-profile', form)
      applySaved(res.data)
      setNotice(res.data.profile_status === 'active' ? 'Profile updated.' : 'Draft saved.')
    } catch (err) {
      setErrors(extractErrors(err))
    } finally {
      setSaving(false)
    }
  }

  /** Save first, then activate — otherwise unsaved edits are not what goes live. */
  async function handleActivate() {
    setActivating(true)
    setErrors([])
    setNotice(null)
    try {
      await apiClient.put<FirmProfile>('/v1/firm-profile', form)
      const res = await apiClient.post<FirmProfile>('/v1/firm-profile/activate')
      applySaved(res.data)
      setNotice('Firm profile is now active. It will appear on client-facing output.')
    } catch (err) {
      setErrors(extractErrors(err))
    } finally {
      setActivating(false)
    }
  }

  if (loading) {
    return <div className="p-6 text-sm text-muted-foreground">Loading firm profile…</div>
  }

  const isActive = status === 'active'
  const busy = saving || activating || uploading

  return (
    <div className="p-4 sm:p-6 max-w-5xl mx-auto space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-xl font-semibold">Firm Profile</h1>
          <p className="text-sm text-muted-foreground">
            Your firm's legal identity — used on report cover pages, engagement letters,
            and client portals.
          </p>
        </div>
        <span
          className={
            'rounded-full px-3 py-1 text-xs font-medium ' +
            (isActive
              ? 'bg-emerald-100 text-emerald-800'
              : 'bg-amber-100 text-amber-800')
          }
        >
          {isActive ? 'Active' : 'Draft'}
        </span>
      </div>

      {needsOnboarding && (
        <Alert>
          <AlertDescription>
            {hasProfile
              ? 'This profile is still a draft. Until it is activated, client-facing documents fall back to the platform default.'
              : 'Your firm has not set up its profile yet. Fill this in once and every report, letter, and portal will carry your firm’s name and license details.'}
          </AlertDescription>
        </Alert>
      )}

      {errors.length > 0 && (
        <Alert variant="destructive">
          <AlertDescription>
            {errors.length === 1 ? (
              errors[0]
            ) : (
              <ul className="list-disc pl-4 space-y-0.5">
                {errors.map(e => <li key={e}>{e}</li>)}
              </ul>
            )}
          </AlertDescription>
        </Alert>
      )}

      {notice && (
        <Alert>
          <AlertDescription>{notice}</AlertDescription>
        </Alert>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <FirmIdentityCard form={form} update={update} licenseStates={licenseStates} />
        <FirmDetailsCards
          form={form}
          update={update}
          licenseStates={licenseStates}
          uploading={uploading}
          onLogoUpload={handleLogoUpload}
        />
      </div>

      <div className="flex flex-wrap items-center gap-3">
        <Button onClick={handleSave} disabled={busy} variant={isActive ? 'default' : 'outline'}>
          {saving ? 'Saving…' : isActive ? 'Save changes' : 'Save draft'}
        </Button>
        {!isActive && (
          <Button onClick={handleActivate} disabled={busy}>
            {activating ? 'Activating…' : 'Save & activate'}
          </Button>
        )}
        {updatedAt && (
          <span className="text-xs text-muted-foreground">
            Last updated {new Date(updatedAt).toLocaleString()}
          </span>
        )}
      </div>
    </div>
  )
}
